import React, { useMemo } from 'react'
import 'ag-grid-enterprise'
import { AgGridReact } from '@ag-grid-community/react' // React Grid Logic
import { ModuleRegistry } from '@ag-grid-community/core'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { ClipboardModule } from '@ag-grid-enterprise/clipboard'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { LicenseManager } from '@ag-grid-enterprise/core'
import 'ag-grid-community/styles/ag-grid.css' // Core CSS
import 'ag-grid-community/styles/ag-theme-quartz.css' // Theme
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping'
import '../../new-table/styles/index.scss'

import { Divider, Flex, Typography } from 'antd'
import { useTableContext } from './TableProvider'
import TableDropdown from './TableColumnDropdown'
import TableNoRowOverlay from './TableNoRowOverlay'
import TableLoadingOverlay from './TableLoadingOverlay'
import styled, { css } from 'styled-components'
import { TableProps } from '../table.model'
import TableUploadButton from './TableUploadButton'
import TableResetButton from './TableResetButton'
import TableAddButton from './TableAddButton'
import TableDownloadButton from './TableDownloadButton'
import TablePagination from './TablePagination'
import TableSearch from './TableSearch'

const WrapperTable: any = styled(AgGridReact<TableProps>)<TableProps>`
  .ag-header-cell-text {
    ${(props) =>
      props.tableHeaderFontColor &&
      css`
        color: ${props.tableHeaderFontColor};
      `}
  }
  .ag-header {
    .ag-pinned-left-header,
    .ag-header-viewport {
      ${(props) =>
        props.tableHeaderBackgroundColor &&
        css`
          background-color: ${props.tableHeaderBackgroundColor};
        `}
    }
  }
`

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_KEY ?? '')

ModuleRegistry.registerModules([ClientSideRowModelModule, ClipboardModule, RangeSelectionModule, RowGroupingModule])

// Create new GridExample component
const TableContent: React.FC = () => {
  const {
    defs,
    tableRef,
    tableName,
    tableDescription,
    rowData,
    columnDefs = [],
    disableTableControl = false,
    groupDefaultExpanded,
    rootContainerStyle,
    tableContainerStyle,
    sortingOrder,
    pagination,
    paginationPageSizeSelector,
    paginationPageSize,
    accentedSort = true,
    onGridReady,
    onSortChanged,
    onColumnResized,
    isSearch,
    suppressNoRowsOverlay,
    tableSearchData,
    customFilter,
    className,
    ...etc
  } = useTableContext()

  const isGroupingTable = useMemo(() => defs.some((item) => item.rowGroup), [defs])

  return (
    <div style={rootContainerStyle}>
      {tableSearchData && customFilter?.(tableSearchData)}
      <Flex id='search-container' gap={8} justify='space-between' align='center'>
        <Typography.Text
          style={{
            fontWeight: 'bold',
            fontSize: 18,
          }}
        >
          {tableName}
        </Typography.Text>
        {tableDescription && (
          <React.Fragment>
            <Divider
              type='vertical'
              style={{
                height: 2,
              }}
            />
            <Typography.Text
              style={{
                fontWeight: '400',
                fontSize: 14,
                color: '#46515A',
              }}
            >
              {tableDescription}
            </Typography.Text>
          </React.Fragment>
        )}
        <Flex gap={8} align='center' justify='end' flex={1}>
          <TableSearch />
          <TableDropdown />
          <TableResetButton />
          {!disableTableControl && <Divider type='vertical' />}
          <TableAddButton />
          <TableUploadButton />
          <TableDownloadButton />
        </Flex>
      </Flex>
      <div
        style={tableContainerStyle}
        className={`ag-theme-quartz ${isGroupingTable ? 'common-group-table' : 'common-table'} ${className}`}
      >
        {/* @ts-ignore */}
        <WrapperTable
          suppressNoRowsOverlay
          hideOverlay
          ref={tableRef}
          rowData={rowData}
          columnDefs={defs}
          pagination={pagination}
          groupDefaultExpanded={groupDefaultExpanded}
          sortingOrder={sortingOrder}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={paginationPageSizeSelector}
          suppressPaginationPanel
          noRowsOverlayComponent={TableNoRowOverlay}
          loadingOverlayComponent={TableLoadingOverlay}
          enableRangeSelection
          accentedSort={accentedSort}
          suppressMovableColumns
          onSortChanged={onSortChanged}
          onColumnResized={onColumnResized}
          onGridReady={onGridReady}
          domLayout={'autoHeight'}
          {...etc}
        />
        <div style={{ marginTop: 16 }}>{!rowData?.length ? null : <TablePagination />}</div>
      </div>
    </div>
  )
}

export default TableContent
